import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, Search, RotateCcw, Save, Plus, Trash2 } from 'lucide-react';
import { API_BASE as API, getToken } from '../../../lib/api';
import { useAttributeEngine } from '../../../hooks/useAttributeEngine';
import GetInfoPopup from '../../../components/GetInfoPopup';
import HBSDate from '../../../utils/HBSDate';
import '../../../styles/item-master.css';
import '../../../styles/getinfo-popup.css';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

/* ─── Empty form state ──────────────────────────────────────────────────────── */
const EMPTY = {
  transNo: '', skuPlu: '', eanCode: '', manufacturerName: '', articleName: '', itemName: '', hsnCode: '',
  agentName: '', agentId: '', supplierName: '', supplierId: '', paymentTerms: 'Credit',
  purchOfficerName: '', purchOfficerId: '', creditPeriod: '30', interest: '1.5',
  priceQty: '1', freeQty: '0', rate: '', mrp: '', wsp: '', discPer: '0',
  purchDisc: '0', ownMrp: '', listMrp: '', freight: '0', octroi: '0',
  otherCharges: '0', commissionPer: '0', tokens: '0', tokensEnabled: false,
  inputTaxName: '', outputTaxName: '', cessValue: '0', baseUnitName: '',
  minQty: '0', rol: '0', maxQty: '0', leadTime: '7', expPeriod: '365',
  hsnTaxRate: '',
  invType: 'inventory', itemStatus: 'active',
  accPurchFrom: '2026-04-01', accPurchTo: '2027-03-31',
  accSalesFrom: '2026-04-01', accSalesTo: '2027-03-31',
  giftVoucher: false, reverseMrp: false, directSplMrp: false, exciseDuty: '0', taxDisplayMode: 'exclusive',
  displayItemRevenue: '', brandingImage: '', promoterSupport: false,
  revenue1: '', revenue2: '', revenue3: '',
  defPurchUnit: '', defSalesUnit: '',
};

/* ─── SVG icons for tabs ────────────────────────────────────────────────────── */
const TAB_ICONS = {
  sku:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5"/></svg>,
  units:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/></svg>,
  rates:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  suppliers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>,
  control:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 10v6m11-7h-6M7 12H1"/></svg>,
  branding:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/></svg>,
  import:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/></svg>,
};
const TABS = [
  { id: 'sku', label: 'SKU / PLU Info' }, { id: 'units', label: 'Multiple Units' },
  { id: 'rates', label: 'Multiple Rates' }, { id: 'suppliers', label: 'Other Suppliers' },
  { id: 'control', label: 'Control Option' }, { id: 'branding', label: 'Branding' },
  { id: 'import', label: 'Import' },
];

/* ─── Barcode visual ────────────────────────────────────────────────────────── */
const BARCODE_HEIGHTS = [100, 60, 90, 40, 100, 70, 50, 95, 65, 100, 45, 80, 100, 55, 75, 85, 35, 90, 60, 100];
function BarcodeVisual({ code }) {
  return (
    <div className="im-barcode-strip">
      <div className="im-barcode-bars">
        {BARCODE_HEIGHTS.map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}
      </div>
      <div className="im-barcode-num">{code || '—'}</div>
    </div>
  );
}

/* ─── Section card ──────────────────────────────────────────────────────────── */
function Section({ title, tag, color, colorLight, icon, children }) {
  const style = {};
  if (color) { style['--sc'] = color; style['--sc-light'] = colorLight || color + '22'; }
  return (
    <div className="im-section">
      <div className="im-section-head" style={style}>
        <div className="im-sh-icon">{icon}</div>
        <span className="im-sh-h3">{title}</span>
        {tag && <span className="im-sh-tag">{tag}</span>}
      </div>
      <div className="im-section-body">{children}</div>
    </div>
  );
}

/* ─── Field helpers ──────────────────────────────────────────────────────────── */
function Field({ label, req, children }) {
  return (
    <div className="im-field">
      <label>{label}{req && <span className="req">*</span>}</label>
      {children}
    </div>
  );
}
function Inp({ label, req, value, onChange, mono, disabled, placeholder, type = 'text' }) {
  return (
    <Field label={label} req={req}>
      <input className={`im-input${mono ? ' mono' : ''}`} type={type} value={value}
        onChange={e => onChange(e.target.value)} disabled={disabled} placeholder={placeholder || ''} />
    </Field>
  );
}
function Num({ label, req, value, onChange, suffix, disabled }) {
  if (suffix) return (
    <Field label={label} req={req}>
      <div className="im-suffix-wrap">
        <input className="im-input mono" type="number" value={value}
          onChange={e => onChange(e.target.value)} disabled={disabled} />
        <span className="im-suffix">{suffix}</span>
      </div>
    </Field>
  );
  return (
    <Field label={label} req={req}>
      <input className="im-input mono" type="number" value={value}
        onChange={e => onChange(e.target.value)} disabled={disabled} />
    </Field>
  );
}
function LkpField({ label, req, value, readonlyVal, onLookup, swatch }) {
  return (
    <Field label={label} req={req}>
      <div className="im-field-btn-row">
        {swatch && <span className="im-swatch-dot" style={{ background: swatch }} />}
        <input className="im-input" readOnly value={readonlyVal || value || ''} placeholder="—" />
        <button type="button" className="im-icon-btn" onClick={onLookup}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/>
          </svg>
        </button>
      </div>
    </Field>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select className="im-select" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}
function Toggle({ label, sub, checked, onChange }) {
  return (
    <div className="im-toggle-row">
      <div className="im-toggle-copy"><b>{label}</b>{sub && <span>{sub}</span>}</div>
      <label className="im-switch">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span className="im-track" />
      </label>
    </div>
  );
}

/* ─── Units row template ─────────────────────────────────────────────────────── */
const EMPTY_UNIT = { unitName: '', qty: '', skuCode: '', eanCode: '' };
const EMPTY_RATE = { unit: '', nos: '', rate: '', salesPrice: '', mrp: '', discEnabled: false, discPer: '0', splMrp: '' };
const EMPTY_SUPP = { suppName: '', suppId: '', leadTime: '', mrp: '', markup: '', markdown: '', landingCost: '', basicCost: '', partNo: '', suppUom: '' };

/* ─── SearchInfo bindings for AE classification attributes (fallback map) ──── */
const ATTR_SEARCHINFO_MAP = {
  division:      { screenId: '5.5.8',  controlId: 1 },
  department:    { screenId: '5.5.11', controlId: 1 },
  category:      { screenId: '5.5.2',  controlId: 1 },
  subcategory:   { screenId: '5.5.12', controlId: 1 },
  brand:         { screenId: '5.5.3',  controlId: 1 },
  style:         { screenId: '5.5.10', controlId: 1 },
  color:         { screenId: '5.5.4',  controlId: 1 },
  size:          { screenId: '5.5.9',  controlId: 1 },
  design:        { screenId: '5.5.5',  controlId: 1 },
  // Tax lookups — use SearchInfoConfig screen 4.8.1
  inputtaxname:  { screenId: '4.8.1',  controlId: 16 },
  outputtaxname: { screenId: '4.8.1',  controlId: 17 },
  baseunitname:  { screenId: '4.8.1',  controlId: 6 },
};

// ─── Safe formula evaluator ───────────────────────────────────────────────────
function evaluateFormula(formula, values) {
  if (!formula?.trim()) return null;
  try {
    let expr = formula.replace(/\{(\w+)\}/g, (_, id) => {
      const v = parseFloat(values?.[id]);
      return isNaN(v) ? '0' : String(v);
    });
    expr = expr.replace(/\bround\s*\(/gi, '__round(')
               .replace(/\babs\s*\(/gi, '__abs(')
               .replace(/\bmin\s*\(/gi, '__min(')
               .replace(/\bmax\s*\(/gi, '__max(')
               .replace(/\bif\s*\(/gi, '__if(');
    // eslint-disable-next-line no-new-func
    const result = new Function('__round','__abs','__min','__max','__if',`"use strict"; return (${expr})`)(
      (x, n) => Math.round(x * Math.pow(10, n == null ? 2 : n)) / Math.pow(10, n == null ? 2 : n),
      Math.abs, Math.min, Math.max,
      (cond, then, els) => cond ? then : els,
    );
    return typeof result === 'number' && isFinite(result) ? result : null;
  } catch { return null; }
}
const precRound = (v, dp = 2) => Math.round(v * Math.pow(10, dp)) / Math.pow(10, dp);

// Native form-state fields that are ALSO AE attrs (bridge: AE rendering reads/writes form, not aeValues)
// This prevents dual-state conflicts for fields that existed before AE was introduced.
const NATIVE_FORM_BRIDGE = new Set([
  'mrp', 'rate', 'wsp', 'priceQty', 'freeQty', 'discPer', 'purchDisc', 'ownMrp', 'listMrp',
  'inputTaxName', 'outputTaxName', 'cessValue', 'hsnTaxRate', 'baseUnitName',
]);

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function ItemMasterDynamic({ theme, onClose, editItemId }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [activeTab, setActiveTab] = useState('sku');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Lookup modal state
  const [lookup, setLookup] = useState(null); // { title, url, cols, onSelect }

  // GetInfo popup state (SearchInfoConfig-driven)
  const [getInfoState, setGetInfoState] = useState(null); // { title, tableName, onSelect }

  // Grid rows
  const [unitRows, setUnitRows] = useState(Array.from({ length: 3 }, (_, i) => ({ ...EMPTY_UNIT, _id: i, fixed: i === 0 })));
  const [rateRows, setRateRows] = useState(Array.from({ length: 3 }, (_, i) => ({ ...EMPTY_RATE, _id: i })));
  const [supplierRows, setSupplierRows] = useState([]);
  const [suppEntry, setSuppEntry] = useState({ ...EMPTY_SUPP });

  // Import
  const [importFile, setImportFile] = useState('');

  // Industry + screen def
  const [selectedIndustry, setSelectedIndustry] = useState(() => localStorage.getItem('item_screens_industry') || 'Apparel');
  const [aeIndustries, setAeIndustries] = useState([]);
  const [companyCountry, setCompanyCountry] = useState('');
  const [aeValues, setAeValues] = useState({});
  const [aeLabels, setAeLabels] = useState({});
  const [screenDef, setScreenDef] = useState(null);

  // AE attribute engine — skipped when screen def has a baked-in AE snapshot
  const aeSnapshot = screenDef?.aeAttributeSnapshot;
  const { attributes: allAeAttrs, industryAttrs, loading: aeLoading, industryGroups, industryByGroup } =
    useAttributeEngine({ industryId: selectedIndustry, skip: !!(aeSnapshot?.attrs?.length) });

  // SKU search / item retrieval
  const [searchSku, setSearchSku] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(editItemId || null);

  /* ── Boot: load industries + country ── */
  useEffect(() => {
    fetch(`${API}/api/attribute-engine/industries`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setAeIndustries(j.data || []); }).catch(() => {});
    fetch(`${API}/api/attribute-engine/region`, { headers: hdrs() })
      .then(r => r.json()).then(j => { if (j.success) setCompanyCountry(j.data?.countryCode || ''); }).catch(() => {});
  }, []);

  /* ── Load screen definition when industry changes ── */
  useEffect(() => {
    if (!selectedIndustry) return;
    fetch(`${API}/api/attribute-engine/screen-definitions/${encodeURIComponent(selectedIndustry)}`, { headers: hdrs() })
      .then(r => r.json())
      .then(j => { if (j.success && j.data?.config) setScreenDef(j.data.config); })
      .catch(() => {});
  }, [selectedIndustry]);

  /* ── Effective AE data: snapshot (no API) if available, live API as fallback ── */
  const effectiveIndustryAttrs = useMemo(() => {
    if (aeSnapshot?.attrs?.length) return aeSnapshot.attrs.filter(a => a.visible !== false); // filter hidden
    return industryAttrs;
  }, [aeSnapshot, industryAttrs]);

  const effectiveIndustryByGroup = useMemo(() => {
    if (aeSnapshot?.attrs?.length) {
      const map = {};
      aeSnapshot.attrs.forEach(a => {
        const gid = a.GroupId || '(none)';
        if (!map[gid]) map[gid] = [];
        map[gid].push(a);
      });
      return map;
    }
    return industryByGroup;
  }, [aeSnapshot, industryByGroup]);

  const effectiveIndustryGroups = useMemo(() => {
    if (aeSnapshot?.groups?.length && aeSnapshot?.attrs?.length) return aeSnapshot.groups;
    return industryGroups;
  }, [aeSnapshot, industryGroups]);

  const effectiveAeLoading = aeSnapshot?.attrs?.length ? false : aeLoading;
  const fieldDbMap = useMemo(() => {
    if (!screenDef) return {};
    const map = {};
    (screenDef.tabs || []).forEach(tab => {
      (tab.sections || []).forEach(sec => {
        (sec.fields || []).forEach(f => {
          if (f && typeof f === 'object' && f.id && f.db) map[f.id] = f.db;
        });
      });
    });
    return map;
  }, [screenDef]);

  /* ── Formula attrs from snapshot ── */
  const formulaAttrs = useMemo(() => {
    if (!aeSnapshot?.attrs) return [];
    return aeSnapshot.attrs.filter(a => a.Formula);
  }, [aeSnapshot]);

  /* ── Auto-evaluate formulas when pricing inputs change ── */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!formulaAttrs.length) return;
    const allVals = { ...form, ...aeValues };
    const formUpdates = {};
    const aeUpdates = {};
    formulaAttrs.forEach(attr => {
      const result = evaluateFormula(attr.Formula, allVals);
      if (result === null) return;
      const dp = attr.decimalPlaces || 2;
      const strVal = precRound(result, dp).toFixed(dp);
      if (NATIVE_FORM_BRIDGE.has(attr.AttributeId)) {
        if (String(form[attr.AttributeId] || '') !== strVal) formUpdates[attr.AttributeId] = strVal;
      } else {
        if (String(aeValues[attr.AttributeId] || '') !== strVal) aeUpdates[attr.AttributeId] = strVal;
      }
    });
    if (Object.keys(formUpdates).length) setForm(prev => ({ ...prev, ...formUpdates }));
    if (Object.keys(aeUpdates).length) setAeValues(prev => ({ ...prev, ...aeUpdates }));
  // Watch only user-editable pricing inputs; formula output fields not watched to prevent loops
  }, [form.mrp, form.rate, form.wsp, form.discPer, form.purchDisc, form.ownMrp, form.listMrp, form.freight, form.otherCharges, form.hsnTaxRate, form.commissionPer]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Cascade: clear child attrs when parent changes ── */
  const cascadeClear = useCallback((parentAttrId) => {
    const children = effectiveIndustryAttrs.filter(a => a.ParentAttributeId === parentAttrId);
    if (!children.length) return;
    // Find the section's hierarchyType for this attr
    const attr = effectiveIndustryAttrs.find(a => a.AttributeId === parentAttrId);
    const gid = attr?.GroupId;
    const skuTab = screenDef?.tabs?.find(t => t.id === 'sku');
    const sec = skuTab?.sections?.find(s => s.includeGroups?.includes(gid));
    const hType = sec?.hierarchyType || 'vertical';
    if (hType === 'horizontal') return; // no auto-cascade for horizontal
    setAeValues(prev => {
      const n = { ...prev };
      children.forEach(c => { n[c.AttributeId] = ''; });
      return n;
    });
    setAeLabels(prev => {
      const n = { ...prev };
      children.forEach(c => { n[c.AttributeId] = ''; });
      return n;
    });
    // Recursively clear grandchildren
    children.forEach(c => cascadeClear(c.AttributeId));
  }, [effectiveIndustryAttrs, screenDef]);

  /* ── Visible tabs: driven by screenDef enabled flags, falls back to all TABS ── */
  const visibleTabs = useMemo(() => {
    if (!screenDef?.tabs?.length) return TABS;
    // Build saved tab map keyed by id
    const savedMap = {};
    screenDef.tabs.forEach(t => { savedMap[t.id] = t; });
    // For each default tab, check if it's explicitly disabled in saved config
    return TABS.filter(defaultTab => {
      const saved = savedMap[defaultTab.id];
      // Not in saved config → show by default; in saved with enabled:false → hide
      return !saved || saved.enabled !== false;
    }).map(defaultTab => {
      const saved = savedMap[defaultTab.id];
      return { ...defaultTab, label: (saved && saved.label) ? saved.label : defaultTab.label };
    });
  }, [screenDef]);

  /* ── Reset activeTab when visible tabs change (e.g. industry switch) ── */
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some(t => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs]);

  /* ── Apply industry-configured default row counts when screen def loads for new items ── */
  useEffect(() => {
    if (!screenDef || editingItemId) return;
    const unitsCount = screenDef.tabs?.find(t => t.id === 'units')?.defaultRowCount || 3;
    const ratesCount = screenDef.tabs?.find(t => t.id === 'rates')?.defaultRowCount || 3;
    setUnitRows(Array.from({ length: unitsCount }, (_, i) => ({ ...EMPTY_UNIT, _id: i, fixed: i === 0 })));
    setRateRows(Array.from({ length: ratesCount }, (_, i) => ({ ...EMPTY_RATE, _id: i })));
  }, [screenDef]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-load when editItemId prop is provided ── */
  useEffect(() => {
    if (!editItemId) return;
    setSearchSku(editItemId);
    (async () => {
      setSearchLoading(true);
      try {
        const r = await fetch(`${API}/api/inventory/items/${encodeURIComponent(editItemId)}`, { headers: hdrs() });
        const j = await r.json();
        if (j.success && j.data) {
          const d = j.data;
          setForm({ ...EMPTY, ...d });
          setEditingItemId(d.itemId || editItemId);
          if (d.aeAttributes?.length) {
            const vals = {}; const lbls = {};
            d.aeAttributes.forEach(a => { vals[a.attrId] = a.value; if (a.label) lbls[a.attrId] = a.label; });
            setAeValues(vals); setAeLabels(lbls);
          }
        }
      } catch {}
      setSearchLoading(false);
    })();
  }, [editItemId]);

  /* ── set helper ── */
  const set = useCallback((k, v) => setForm(p => ({ ...p, [k]: v })), []);

  /* ── MRP markdown auto-calc ── */
  const mrpMarkdown = useMemo(() => {
    const mrp = parseFloat(form.mrp) || 0;
    const rate = parseFloat(form.rate) || 0;
    return mrp > 0 ? ((mrp - rate) / mrp * 100).toFixed(2) : '0.00';
  }, [form.mrp, form.rate]);

  const wspMarkdown = useMemo(() => {
    const mrp = parseFloat(form.mrp) || 0;
    const wsp = parseFloat(form.wsp) || 0;
    return mrp > 0 ? ((mrp - wsp) / mrp * 100).toFixed(2) : '0.00';
  }, [form.mrp, form.wsp]);

  const margin = useMemo(() => {
    const mrp = parseFloat(form.mrp) || 0;
    const rate = parseFloat(form.rate) || 0;
    return mrp > 0 ? ((mrp - rate) / mrp * 100).toFixed(1) : '0.0';
  }, [form.mrp, form.rate]);

  /* ── Stock fill % for preview ── */
  const stockFill = useMemo(() => {
    const max = parseFloat(form.maxQty) || 100;
    const rol = parseFloat(form.rol) || 0;
    return max > 0 ? Math.min(100, (rol / max) * 100).toFixed(0) : 0;
  }, [form.maxQty, form.rol]);

  /* ── Profile strength ── */
  const profileStrength = useMemo(() => {
    const fields = ['skuPlu', 'itemName', 'eanCode', 'supplierName', 'mrp', 'rate', 'hsnCode', 'inputTaxName', 'outputTaxName', 'agentName'];
    const filled = fields.filter(f => form[f]?.toString().trim()).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  /* ── Handle Clear ── */
  const handleClear = () => {
    setForm({ ...EMPTY }); setAeValues({}); setAeLabels({});
    setEditingItemId(null); setSearchSku(''); setError('');
    setUnitRows(Array.from({ length: screenDef?.tabs?.find(t => t.id === 'units')?.defaultRowCount || 3 }, (_, i) => ({ ...EMPTY_UNIT, _id: i, fixed: i === 0 })));
    setRateRows(Array.from({ length: screenDef?.tabs?.find(t => t.id === 'rates')?.defaultRowCount || 3 }, (_, i) => ({ ...EMPTY_RATE, _id: i })));
    setSupplierRows([]);
  };

  /* ── Item retrieval ── */
  const handleLoadItem = useCallback(async () => {
    if (!searchSku.trim()) return;
    setSearchLoading(true); setError('');
    try {
      const r = await fetch(`${API}/api/inventory/items/${encodeURIComponent(searchSku.trim())}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success && j.data) {
        const d = j.data;
        setForm({ ...EMPTY, ...d });
        setEditingItemId(d.itemId || d.articleCode || searchSku.trim());
        if (d.aeAttributes?.length) {
          const vals = {}; const lbls = {};
          d.aeAttributes.forEach(a => { vals[a.attrId] = a.value; if (a.label) lbls[a.attrId] = a.label; });
          setAeValues(vals); setAeLabels(lbls);
        }
        setActiveTab('sku');
      } else {
        setError(`Item "${searchSku}" not found`);
      }
    } catch { setError('Search failed. Check connection.'); }
    setSearchLoading(false);
  }, [searchSku]);

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.itemName?.trim()) { setError('Item Name is required'); return; }
    setSaving(true); setError('');
    try {
      const aeAttributes = Object.entries(aeValues).map(([attrId, value]) => ({
        attrId, value: value?.toString() || '', label: aeLabels[attrId] || attrId,
      }));
      const payload = { ...form, aeAttributes, supplierRows, unitRows, rateRows };
      const method = editingItemId ? 'PUT' : 'POST';
      const url = editingItemId ? `${API}/api/inventory/items/${editingItemId}` : `${API}/api/inventory/items`;
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        if (!editingItemId) { setEditingItemId(j.itemId || j.itemCode); }
        setError('');
      } else { setError(j.message || j.error || 'Save failed'); }
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  /* ── GetInfo popup opener — accepts string tableName or config object ── */
  const openGetInfo = useCallback((title, tableOrConfig, onSelect) => {
    const cfg = typeof tableOrConfig === 'string' ? { tableName: tableOrConfig } : (tableOrConfig || {});
    setGetInfoState({ title, ...cfg, onSelect });
  }, []);

  /* ─── Section icon SVGs ─────────────────────────────────────────────────────── */
  const iconProcurement = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>;
  const iconProduct = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/></svg>;
  const iconClassify = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>;
  const iconPricing  = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
  const iconTax      = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" x2="5" y1="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>;
  const iconControl  = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="m9 12 2 2 4-4"/></svg>;
  const iconSettings = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 10v6m11-7h-6M7 12H1"/></svg>;
  const iconEntry    = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
  const iconBranding = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/></svg>;

  /* ─── Render a single AE attribute field ─────────────────────────────────── */
  const renderAEAttr = (attr) => {
    const isLookup = attr.ControlType === 'LookupEdit' || attr.ControlType === 'LookUpEdit'
      || attr.ControlType === 'ComboBoxEdit';
    const isDate  = attr.ControlType === 'DateEdit';
    const isHBS   = !!attr.IsDateHBS; // store/retrieve as HBS integer
    const isColour = attr.AttributeId?.toLowerCase().includes('colour')
      || attr.Label?.toLowerCase().includes('colour') || attr.Label?.toLowerCase().includes('color');

    // IsMandatory: combine all mandatory signals (DB column + IsRequired + country)
    const isCountryReq = companyCountry && isRequiredInCountry(attr.AttributeId, companyCountry);
    const isMandatory = !!attr.IsMandatory || !!attr.IsRequired || isCountryReq;

    // 'NA' default: show NA placeholder for new opted-in attrs with no value
    const rawVal = useFormBridge => useFormBridge ? form[attr.AttributeId] : aeValues[attr.AttributeId];
    const useFormBridgeFlag = NATIVE_FORM_BRIDGE.has(attr.AttributeId);
    const currentRaw = useFormBridgeFlag ? (form[attr.AttributeId] || '') : (aeValues[attr.AttributeId] || '');
    const hasNoValue = currentRaw === undefined || currentRaw === null || currentRaw === '';
    const naDefault  = hasNoValue && (attr.DefaultValue === 'NA' || attr.DefaultValue === 'na');

    const hexVal = !useFormBridgeFlag ? (aeValues[attr.AttributeId] || '') : '';
    const isHex  = /^#[0-9A-Fa-f]{3,6}$/.test(hexVal);

    // For DateEdit with IsDateHBS: convert HBS int ↔ ISO date string for input
    let displayVal;
    if (isDate && isHBS && currentRaw) {
      displayVal = HBSDate.toISO(parseInt(currentRaw, 10)) || currentRaw;
    } else if (useFormBridgeFlag) {
      displayVal = form[attr.AttributeId] || '';
    } else if (isLookup) {
      displayVal = aeLabels[attr.AttributeId] || aeValues[attr.AttributeId] || '';
    } else {
      displayVal = aeValues[attr.AttributeId] || '';
    }

    const attrKey = attr.AttributeId?.toLowerCase();
    const dbCfg = fieldDbMap[attr.AttributeId] || fieldDbMap[attrKey] || ATTR_SEARCHINFO_MAP[attrKey];
    const lookupCfg = dbCfg?.screenId ? { screenId: dbCfg.screenId, controlId: dbCfg.controlId } : attr.SourceTable;

    const handleChange = useFormBridgeFlag
      ? e => set(attr.AttributeId, e.target.value)
      : (isLookup ? undefined : e => {
          const newVal = e.target.value;
          if (isDate && isHBS) {
            // Convert ISO date → HBS integer for storage
            const hbsInt = newVal ? HBSDate.fromString(newVal) : '';
            setAeValues(p => ({ ...p, [attr.AttributeId]: hbsInt ? String(hbsInt) : '' }));
          } else {
            setAeValues(p => ({ ...p, [attr.AttributeId]: newVal }));
            cascadeClear(attr.AttributeId);
          }
        });

    const handleLookupSelect = useFormBridgeFlag
      ? (row, cols, metaInfo) => {
          const valCol  = metaInfo?.valueMember  || cols.find(c => c.isValue)?.field  || cols[0]?.field;
          const nameCol = metaInfo?.displayMember || cols.find(c => c.isDisplay)?.field || cols[0]?.field;
          set(attr.AttributeId, row[nameCol] || row[valCol] || '');
          cascadeClear(attr.AttributeId);
        }
      : (row, cols, metaInfo) => {
          const valCol  = metaInfo?.valueMember  || cols.find(c => c.isValue)?.field  || cols[0]?.field;
          const nameCol = metaInfo?.displayMember || cols.find(c => c.isDisplay)?.field || cols[0]?.field;
          setAeValues(p => ({ ...p, [attr.AttributeId]: row[valCol] || row[nameCol] || '' }));
          setAeLabels(p => ({ ...p, [attr.AttributeId]: row[nameCol] || row[valCol] || '' }));
          cascadeClear(attr.AttributeId);
        };

    const isReadonly = attr.AttributeId === 'hsnTaxRate';

    // DateEdit rendering
    if (isDate) {
      return (
        <Field key={attr.AttributeId} label={attr.Label} req={isMandatory}>
          <div style={{ position: 'relative' }}>
            <input type="date" className="im-input"
              value={displayVal}
              onChange={handleChange}
              placeholder={naDefault ? 'NA' : ''}
              style={{ borderColor: isMandatory && !displayVal ? '#dc2626' : undefined }} />
            {isHBS && <span style={{ position:'absolute', right:28, top:'50%', transform:'translateY(-50%)', fontSize:8, color:'var(--ink-faint)', pointerEvents:'none' }}>HBS</span>}
            {naDefault && !displayVal && <span style={{ fontSize: 9, color: 'var(--ink-faint)', fontStyle: 'italic', display: 'block' }}>NA</span>}
          </div>
        </Field>
      );
    }

    return (
      <Field key={attr.AttributeId} label={attr.Label} req={isMandatory}>
        <div className="im-field-btn-row" style={{ alignItems: 'center' }}>
          {isColour && isHex && <span className="im-swatch-dot" style={{ background: hexVal }} />}
          <input className="im-input" value={displayVal}
            readOnly={isLookup || isReadonly}
            disabled={isReadonly}
            onChange={isLookup || isReadonly ? undefined : handleChange}
            placeholder={naDefault ? 'NA' : (attr.Placeholder || '—')} />
          {isLookup && lookupCfg && (
            <button type="button" className="im-icon-btn"
              onClick={() => openGetInfo(attr.Label, lookupCfg, handleLookupSelect)}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg>
            </button>
          )}
          {naDefault && !displayVal && <span style={{ fontSize: 9, color: 'var(--ink-faint)', fontStyle: 'italic' }}>NA</span>}
        </div>
      </Field>
    );
  };

  /* ─── AE Classification renderer (flat fallback) ──────────────────────────── */
  const renderAEClassification = () => {
    const attrs = effectiveIndustryAttrs?.length ? effectiveIndustryAttrs : (allAeAttrs || []);
    if (!attrs.length) return null;
    return <div className="im-grid3">{attrs.map(a => renderAEAttr(a))}</div>;
  };

  /* ─── AE sections split by group ──────────────────────────────────────────── */
  const GROUP_COLORS = {
    grp_classification: { color: '#4C4FCB', light: '#EBEBFB' },
    grp_style:          { color: '#0E8C7F', light: '#E4F5F2' },
    grp_physical:       { color: '#2E6BE0', light: '#EAF1FE' },
    grp_compliance:     { color: '#B5760D', light: '#FBF0DE' },
    grp_pricing:        { color: '#C0392B', light: '#FBEAE8' },
    grp_tax:            { color: '#7C3AED', light: '#F3EFFE' },
  };

  const renderAEByGroup = (includeGroups = null) => {
    const loadingBlock = (
      <Section title="Classification" color="#4C4FCB" colorLight="#EBEBFB" icon={iconClassify}>
        <div style={{ fontSize: 10, color: 'var(--ink-faint)', padding: 8 }}>Loading attributes…</div>
      </Section>
    );
    if (effectiveAeLoading) return loadingBlock;
    const groupIds = Object.keys(effectiveIndustryByGroup);
    // Fall back to flat rendering if groups not yet loaded
    if (!groupIds.length) {
      if (!effectiveIndustryAttrs.length) return loadingBlock;
      return (
        <Section title="Classification" tag="Group & Categories" color="#4C4FCB" colorLight="#EBEBFB" icon={iconClassify}>
          {renderAEClassification()}
        </Section>
      );
    }
    return (
      <>
        {effectiveIndustryGroups
          .filter(group => {
            const gid = group.GroupId || group.groupId;
            // All groups (incl. grp_pricing, grp_tax) are now AE-driven — no static exclusions
            if (includeGroups && !includeGroups.includes(gid)) return false;
            return true;
          })
          .map(group => {
          const gid = group.GroupId || group.groupId;
          const attrs = effectiveIndustryByGroup[gid] || [];
          if (!attrs.length) return null;
          const { color, light } = GROUP_COLORS[gid] || { color: '#64748b', light: '#f8faff' };
          return (
            <Section key={gid} title={group.Label || group.label} color={color} colorLight={light} icon={iconClassify}>
              <div className="im-grid3">
                {attrs.map(a => renderAEAttr(a))}
              </div>
            </Section>
          );
        })}
      </>
    );
  };

  /* ─── SKU Tab ─────────────────────────────────────────────────────────────── */
  const renderSKUTab = () => (
    <>
      {/* Procurement + Product ID side by side */}
      <div className="im-row-2col">
        <Section title="Procurement Details" color="#B5760D" colorLight="#FBF0DE" icon={iconProcurement}>
          <div className="im-grid2">
            <LkpField label="Agent" value={form.agentName} readonlyVal={form.agentName}
              onLookup={() => openGetInfo('Agent', 'AgentMaster', (row, cols) => {
                const idCol = cols.find(c => c.isValue)?.field;
                const nameCol = cols.find(c => c.isDisplay)?.field;
                setForm(p => ({ ...p, agentName: row[nameCol] || '', agentId: row[idCol] || '' }));
              })} />
            <LkpField label="Supplier" req value={form.supplierName} readonlyVal={form.supplierName}
              onLookup={() => openGetInfo('Supplier', { screenId: '3.2', controlId: 5 }, (row, cols, metaInfo) => {
                const valCol = metaInfo?.valueMember || cols.find(c => c.isValue)?.field;
                const dispCol = metaInfo?.displayMember || cols.find(c => c.isDisplay)?.field;
                setForm(p => ({ ...p, supplierName: row[dispCol] || '', supplierId: row[valCol] || '' }));
              })} />
            <SelectField label="Payment Terms" value={form.paymentTerms} onChange={v => set('paymentTerms', v)} options={['Credit', 'Cash', 'Advance']} />
            <LkpField label="Purch. Officer" value={form.purchOfficerName} readonlyVal={form.purchOfficerName}
              onLookup={() => openGetInfo('Purchase Officer', 'purchaseofficermaster', (row, cols) => {
                const idCol = cols.find(c => c.isValue)?.field;
                const nameCol = cols.find(c => c.isDisplay)?.field;
                setForm(p => ({ ...p, purchOfficerName: row[nameCol] || '', purchOfficerId: row[idCol] || '' }));
              })} />
            <Num label="Credit Period" value={form.creditPeriod} onChange={v => set('creditPeriod', v)} suffix="days" />
            <Num label="Interest %" value={form.interest} onChange={v => set('interest', v)} suffix="%" />
          </div>
        </Section>

        <Section title="Product Identification" tag="Trans No · Auto" color="#2E6BE0" colorLight="#EAF1FE" icon={iconProduct}>
          <div className="im-grid3">
            <Inp label="Trans No" value={form.transNo || 'PM-2026-AUTO'} onChange={v => set('transNo', v)} mono disabled />
            <Inp label="SKU / PLU" req value={form.skuPlu} onChange={v => set('skuPlu', v)} mono />
            <Inp label="EAN / OEM Barcode" value={form.eanCode} onChange={v => set('eanCode', v)} mono />
            <LkpField label="Manufacturer" value={form.manufacturerName} readonlyVal={form.manufacturerName}
              onLookup={() => openGetInfo('Manufacturer', 'ManufacturerMaster', (row, cols) => {
                const nameCol = cols.find(c => c.isDisplay)?.field;
                setForm(p => ({ ...p, manufacturerName: row[nameCol] || '' }));
              })} />
            <Inp label="Article Name" value={form.articleName} onChange={v => set('articleName', v)} />
            <Inp label="Item Name" req value={form.itemName} onChange={v => set('itemName', v)} />
            <div className="im-span3">
              <LkpField label="HSN Code" value={form.hsnCode} readonlyVal={form.hsnCode}
                onLookup={() => openGetInfo('HSN Code', { screenId: '22.2.4', controlId: 4 }, async (row, cols, meta) => {
                  const valCol = meta?.valueMember || cols.find(c => c.isValue)?.field || cols[0]?.field;
                  const hsnCode = row[valCol] || row['HSNCode'] || '';
                  const baseTax = row['BaseTax'] != null ? parseFloat(row['BaseTax']) : null;
                  set('hsnCode', hsnCode);
                  if (baseTax != null && !isNaN(baseTax)) {
                    set('hsnTaxRate', String(baseTax));
                    set('cessValue', String(baseTax));
                    // Auto-fill Input / Output Tax names by matching BaseTax rate
                    try {
                      const [inRes, outRes] = await Promise.all([
                        fetch(`${API}/api/inventory/getinfo/screen/4.8.1/control/16`, { headers: hdrs() }),
                        fetch(`${API}/api/inventory/getinfo/screen/4.8.1/control/17`, { headers: hdrs() }),
                      ]);
                      const [inData, outData] = await Promise.all([inRes.json(), outRes.json()]);
                      // Match by numeric Tax column (prefer Exclusive)
                      const findTax = (rows) =>
                        (rows || []).find(r => Number(r['Tax']) === baseTax && String(r['TaxType'] || '').includes('Exclusive'))
                        || (rows || []).find(r => Number(r['Tax']) === baseTax);
                      const inRow  = findTax(inData.data  || inData.rows  || []);
                      const outRow = findTax(outData.data || outData.rows || []);
                      const dispIn  = (inData.columns  || []).find(c => c.isDisplay)?.field || inData.displayMember  || 'Tax';
                      const dispOut = (outData.columns || []).find(c => c.isDisplay)?.field || outData.displayMember || 'Tax';
                      if (inRow)  set('inputTaxName',  String(inRow[dispIn]   ?? inRow['Tax']  ?? ''));
                      if (outRow) set('outputTaxName', String(outRow[dispOut] ?? outRow['Tax'] ?? ''));
                    } catch {} // silent — tax auto-fill is best-effort
                  }
                })} />
            </div>
          </div>
        </Section>
      </div>

      {/* AE Classification sections (split by group) — reads includeGroups from screenDef */}
      {(() => {
        const skuSections = screenDef?.tabs?.find(t => t.id === 'sku')?.sections || [];
        const classSection   = skuSections.find(s => s.type === 'ae-classification' && s.id === 'classification');
        const procSection    = skuSections.find(s => s.type === 'ae-classification' && (s.id === 'product_attributes' || s.id === 'procurement_ae'));
        const pricingSection = skuSections.find(s => s.type === 'ae-classification' && s.id === 'pricing');
        const taxSection     = skuSections.find(s => s.type === 'ae-classification' && s.id === 'tax');
        return (
          <>
            {renderAEByGroup(classSection?.includeGroups || null)}
            {procSection?.includeGroups && renderAEByGroup(procSection.includeGroups)}
          </>
        );
      })()}

      {/* Pricing — AE-driven from grp_pricing snapshot */}
      {(() => {
        const skuSections = screenDef?.tabs?.find(t => t.id === 'sku')?.sections || [];
        const pricingSection = skuSections.find(s => s.id === 'pricing');
        // Check if AE-driven (new) or static fallback (legacy screen defs without type)
        if (pricingSection?.type === 'ae-classification') {
          const priceGroups = pricingSection.includeGroups || ['grp_pricing'];
          return renderAEByGroup(priceGroups);
        }
        // Static fallback (no screen def loaded yet)
        return (
          <Section title="Pricing" color="#0E8C7F" colorLight="#E4F5F2" icon={iconPricing}>
            <div className="im-grid6">
              <Num label="Qty" req value={form.priceQty} onChange={v => set('priceQty', v)} />
              <Num label="Free Qty" value={form.freeQty} onChange={v => set('freeQty', v)} />
              <Num label="Rate (Cost)" value={form.rate} onChange={v => set('rate', v)} />
              <Num label="MRP" req value={form.mrp} onChange={v => set('mrp', v)} />
              <Num label="Sales Price" req value={form.wsp} onChange={v => set('wsp', v)} />
              <Num label="Disc %" value={form.discPer} onChange={v => set('discPer', v)} suffix="%" />
              <Num label="Purch Disc %" value={form.purchDisc} onChange={v => set('purchDisc', v)} suffix="%" />
              <Num label="Own MRP" value={form.ownMrp} onChange={v => set('ownMrp', v)} />
              <Num label="List MRP" value={form.listMrp} onChange={v => set('listMrp', v)} />
            </div>
          </Section>
        );
      })()}

      {/* Markdown strip — always visible alongside pricing */}
      <div className="im-markdown-strip">
        <span className="im-mstat">Sales Price Markdown <b>{wspMarkdown}%</b></span>
        <span className="im-mstat">MRP Markdown <b>{mrpMarkdown}%</b></span>
      </div>

      {/* Others (freight, commission etc.) — static operational fields */}
      <Section title="Other Charges" color="#0E8C7F" colorLight="#E4F5F2" icon={iconPricing}>
        <div className="im-grid6">
          <Num label="Freight" value={form.freight} onChange={v => set('freight', v)} />
          <Num label="Octroi" value={form.octroi} onChange={v => set('octroi', v)} />
          <Num label="Other Charges" value={form.otherCharges} onChange={v => set('otherCharges', v)} />
          <Num label="Commission %" value={form.commissionPer} onChange={v => set('commissionPer', v)} suffix="%" />
          <Field label={<><input type="checkbox" checked={form.tokensEnabled} onChange={e => set('tokensEnabled', e.target.checked)} style={{ width: 'auto' }} /> Tokens</>}>
            <input className="im-input mono" type="number" value={form.tokens} onChange={e => set('tokens', e.target.value)} disabled={!form.tokensEnabled} placeholder="0.00" />
          </Field>
        </div>
        <button type="button" className="im-add-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
          Add custom field
        </button>
      </Section>

      {/* Inventory threshold strip */}
      <div className="im-thresh-strip">
        {[['Min Qty', 'minQty', ''], ['ROL', 'rol', ''], ['Max Qty', 'maxQty', ''], ['Lead Time', 'leadTime', 'd'], ['Exp Period', 'expPeriod', 'd']].map(([lbl, key, sfx]) => (
          <div key={key} className="im-thresh-item">
            <label>{lbl}</label>
            <input type="number" className="im-input mono" value={form[key]} onChange={e => set(key, e.target.value)} />
            {sfx && <span className="im-thresh-suffix">{sfx}</span>}
          </div>
        ))}
      </div>

      {/* Tax & Compliance — AE-driven from grp_tax snapshot */}
      {(() => {
        const skuSections = screenDef?.tabs?.find(t => t.id === 'sku')?.sections || [];
        const taxSection = skuSections.find(s => s.id === 'tax');
        if (taxSection?.type === 'ae-classification') {
          const taxGroups = taxSection.includeGroups || ['grp_tax'];
          return renderAEByGroup(taxGroups);
        }
        // Static fallback
        return (
          <Section title="Tax Configuration" color="#C0392B" colorLight="#FBEAE8" icon={iconTax}>
            <div className="im-grid2">
              <LkpField label="Input Tax (Purchase)" value={form.inputTaxName} readonlyVal={form.inputTaxName}
                onLookup={() => openGetInfo('Input Tax', { screenId: '4.8.1', controlId: 16 }, (row, cols, meta) => {
                  const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field;
                  const valCol  = meta?.valueMember  || cols.find(c => c.isValue)?.field;
                  set('inputTaxName', row[nameCol] || row[valCol] || '');
                })} />
              <LkpField label="Output Tax (Sales)" value={form.outputTaxName} readonlyVal={form.outputTaxName}
                onLookup={() => openGetInfo('Output Tax', { screenId: '4.8.1', controlId: 17 }, (row, cols, meta) => {
                  const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field;
                  const valCol  = meta?.valueMember  || cols.find(c => c.isValue)?.field;
                  set('outputTaxName', row[nameCol] || row[valCol] || '');
                })} />
              <Num label="Cess" value={form.cessValue} onChange={v => set('cessValue', v)} suffix="%" />
              <Field label="HSN Tax Rate">
                <input className="im-input mono" value={form.hsnTaxRate ? `${form.hsnTaxRate}%` : '—'} readOnly disabled />
              </Field>
              <LkpField label="Base Unit" value={form.baseUnitName} readonlyVal={form.baseUnitName}
                onLookup={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (row, cols, meta) => {
                  const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field;
                  set('baseUnitName', row[nameCol] || '');
                })} />
            </div>
          </Section>
        );
      })()}
    </>
  );

  /* ─── Multiple Units Tab ─────────────────────────────────────────────────── */
  const renderUnitsTab = () => (
    <Section title="Additional Units" tag="Pack / case / carton conversions" color="#4C4FCB" colorLight="#EBEBFB"
      icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/></svg>}>
      <div className="im-table-wrap">
        <table className="im-mini-table">
          <thead><tr><th>Unit</th><th>Unit Name</th><th className="cell-tight">=</th><th>No. of Qty</th><th>SKU Code</th><th>EAN / OEM Code</th></tr></thead>
          <tbody>
            {unitRows.map((row, i) => (
              <tr key={row._id} className={!row.unitName && i > 0 ? 'row-muted' : ''}>
                <td className="row-label">Addl Unit {i + 1}</td>
                <td>
                  <div className="lookup-cell">
                    <input className="im-input" value={row.unitName} disabled={row.fixed} placeholder="—"
                      onChange={e => { const n = [...unitRows]; n[i].unitName = e.target.value; setUnitRows(n); }} />
                    {!row.fixed && <button type="button" className="im-icon-btn" onClick={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (r2, cols, meta) => { const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field; const n = [...unitRows]; n[i].unitName = r2[nameCol] || ''; setUnitRows(n); })}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg></button>}
                  </div>
                </td>
                <td className="cell-tight">=</td>
                <td><input className="im-input mono" value={row.qty} disabled={row.fixed} placeholder="0"
                  onChange={e => { const n = [...unitRows]; n[i].qty = e.target.value; setUnitRows(n); }} /></td>
                <td><input className="im-input mono" value={row.skuCode || (row.fixed ? form.skuPlu : '')} disabled={row.fixed} placeholder="—"
                  onChange={e => { const n = [...unitRows]; n[i].skuCode = e.target.value; setUnitRows(n); }} /></td>
                <td><input className="im-input mono" value={row.eanCode || (row.fixed ? '—' : '')} disabled={row.fixed} placeholder="—"
                  onChange={e => { const n = [...unitRows]; n[i].eanCode = e.target.value; setUnitRows(n); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="im-grid2" style={{ marginTop: 12 }}>
        <LkpField label="Default Purchase Unit" value={form.defPurchUnit} readonlyVal={form.defPurchUnit}
          onLookup={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (r2, cols, meta) => { const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field; set('defPurchUnit', r2[nameCol] || ''); })} />
        <LkpField label="Default Sales Unit" value={form.defSalesUnit} readonlyVal={form.defSalesUnit}
          onLookup={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (r2, cols, meta) => { const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field; set('defSalesUnit', r2[nameCol] || ''); })} />
      </div>
    </Section>
  );

  /* ─── Multiple Rates Tab ─────────────────────────────────────────────────── */
  const renderRatesTab = () => (
    <Section title="Price List" tag="Tiered pricing by unit" color="#0E8C7F" colorLight="#E4F5F2" icon={iconPricing}>
      <div className="im-table-wrap">
        <table className="im-mini-table">
          <thead><tr><th>Price</th><th>Unit</th><th>No's</th><th>Rate</th><th>Sales Price</th><th>MRP</th><th>Disc %</th><th>Spl. MRP</th></tr></thead>
          <tbody>
            {rateRows.map((row, i) => {
              const splMrp = row.discEnabled ? ((parseFloat(row.mrp) || 0) * (1 - (parseFloat(row.discPer) || 0) / 100)).toFixed(2) : row.mrp || '';
              return (
                <tr key={row._id} className={!row.unit && i > 0 ? 'row-muted' : ''}>
                  <td className="row-label">Price {i + 1}</td>
                  <td><div className="lookup-cell">
                    <input className="im-input" value={row.unit} readOnly placeholder="—" />
                    <button type="button" className="im-icon-btn"
                      onClick={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (r2, cols, meta) => {
                        const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field;
                        const n = [...rateRows]; n[i].unit = r2[nameCol] || ''; setRateRows(n);
                      })}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg>
                    </button>
                  </div></td>
                  <td><input className="im-input mono" value={row.nos} placeholder="0"
                    onChange={e => { const n = [...rateRows]; n[i].nos = e.target.value; setRateRows(n); }} /></td>
                  <td><input className="im-input mono" value={row.rate} placeholder="0.00"
                    onChange={e => { const n = [...rateRows]; n[i].rate = e.target.value; setRateRows(n); }} /></td>
                  <td><input className="im-input mono" value={row.salesPrice} placeholder="0.00"
                    onChange={e => { const n = [...rateRows]; n[i].salesPrice = e.target.value; setRateRows(n); }} /></td>
                  <td><input className="im-input mono" value={row.mrp} placeholder="0.00"
                    onChange={e => { const n = [...rateRows]; n[i].mrp = e.target.value; setRateRows(n); }} /></td>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" checked={row.discEnabled} style={{ width: 'auto' }}
                        onChange={e => { const n = [...rateRows]; n[i].discEnabled = e.target.checked; setRateRows(n); }} />
                      <input className="im-input mono" value={row.discPer} placeholder="0" style={{ width: 44 }}
                        onChange={e => { const n = [...rateRows]; n[i].discPer = e.target.value; setRateRows(n); }} />
                    </label>
                  </td>
                  <td><input className="im-input mono" value={splMrp} disabled placeholder="0.00" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );

  /* ─── Suppliers Tab ──────────────────────────────────────────────────────── */
  const renderSuppliersTab = () => (
    <Section title="Other Suppliers" tag="Alternate sourcing options" color="#B5760D" colorLight="#FBF0DE" icon={iconProcurement}>
      {/* Entry form */}
      <div className="im-grid4" style={{ marginBottom: 4 }}>
        <div className="im-span2">
          <LkpField label="Supplier" req value={suppEntry.suppName} readonlyVal={suppEntry.suppName}
            onLookup={() => openGetInfo('Supplier', { screenId: '3.2', controlId: 5 }, (row, cols, metaInfo) => {
              const valCol = metaInfo?.valueMember || cols.find(c => c.isValue)?.field;
              const dispCol = metaInfo?.displayMember || cols.find(c => c.isDisplay)?.field;
              setSuppEntry(p => ({ ...p, suppName: row[dispCol] || '', suppId: row[valCol] || '' }));
            })} />
        </div>
        <Num label="Lead Time" value={suppEntry.leadTime} onChange={v => setSuppEntry(p => ({ ...p, leadTime: v }))} suffix="d" />
        <Num label="MRP" value={suppEntry.mrp} onChange={v => setSuppEntry(p => ({ ...p, mrp: v }))} />
        <Num label="Markup %" value={suppEntry.markup} onChange={v => setSuppEntry(p => ({ ...p, markup: v }))} />
        <Num label="Markdown %" value={suppEntry.markdown} onChange={v => setSuppEntry(p => ({ ...p, markdown: v }))} />
        <Num label="Landing Cost" value={suppEntry.landingCost} onChange={v => setSuppEntry(p => ({ ...p, landingCost: v }))} />
        <Num label="Basic Cost" value={suppEntry.basicCost} onChange={v => setSuppEntry(p => ({ ...p, basicCost: v }))} />
        <LkpField label="UOM" value={suppEntry.suppUom} readonlyVal={suppEntry.suppUom}
          onLookup={() => openGetInfo('UOM', { screenId: '4.8.1', controlId: 6 }, (r2, cols, meta) => {
            const nameCol = meta?.displayMember || cols.find(c => c.isDisplay)?.field;
            setSuppEntry(p => ({ ...p, suppUom: r2[nameCol] || '' }));
          })} />
        <div className="im-span2">
          <Inp label="Part No" value={suppEntry.partNo} onChange={v => setSuppEntry(p => ({ ...p, partNo: v }))} placeholder="Supplier part / reference number" />
        </div>
      </div>
      <button type="button" className="im-add-btn" style={{ marginBottom: 12 }}
        onClick={() => {
          if (!suppEntry.suppName) return;
          setSupplierRows(p => [...p, { ...suppEntry, _id: Date.now() }]);
          setSuppEntry({ ...EMPTY_SUPP });
        }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 5v14M5 12h14"/></svg>
        Add to supplier list
      </button>
      {/* Table */}
      <div className="im-table-wrap">
        <table className="im-mini-table">
          <thead><tr><th>Supp Id</th><th>Supplier Name</th><th>Lead Time</th><th>MRP</th><th>Mark Up</th><th>Mark Down</th><th>Landing Cost</th><th>Basic Cost</th><th>UOM</th><th></th></tr></thead>
          <tbody>
            {supplierRows.length === 0
              ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: 14, fontSize: 10, color: 'var(--ink-faint)' }}>No alternate suppliers added yet</td></tr>
              : supplierRows.map((r, i) => (
                <tr key={r._id}>
                  <td className="row-label">{r.suppId || '—'}</td>
                  <td>{r.suppName}</td>
                  <td><input className="im-input mono" value={r.leadTime} style={{ width: 50 }}
                    onChange={e => { const n = [...supplierRows]; n[i].leadTime = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input mono" value={r.mrp} style={{ width: 70 }}
                    onChange={e => { const n = [...supplierRows]; n[i].mrp = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input mono" value={r.markup} style={{ width: 50 }}
                    onChange={e => { const n = [...supplierRows]; n[i].markup = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input mono" value={r.markdown} style={{ width: 60 }}
                    onChange={e => { const n = [...supplierRows]; n[i].markdown = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input mono" value={r.landingCost} style={{ width: 80 }}
                    onChange={e => { const n = [...supplierRows]; n[i].landingCost = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input mono" value={r.basicCost} style={{ width: 80 }}
                    onChange={e => { const n = [...supplierRows]; n[i].basicCost = e.target.value; setSupplierRows(n); }} /></td>
                  <td><input className="im-input" value={r.suppUom || '—'} readOnly style={{ width: 60 }} /></td>
                  <td>
                    <button type="button" className="im-icon-btn" onClick={() => setSupplierRows(p => p.filter((_, j) => j !== i))}
                      style={{ color: 'var(--rose)', borderColor: 'var(--rose-light)' }}>
                      <Trash2 size={10} />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="im-supp-footer">
        <span className="im-supp-count">Total No. of Suppliers: <b>{supplierRows.length}</b></span>
        <button type="button" className="im-btn im-btn-primary" style={{ fontSize: 11, padding: '6px 14px' }}>Update Suppliers</button>
      </div>
    </Section>
  );

  /* ─── Control Tab ────────────────────────────────────────────────────────── */
  const renderControlTab = () => (
    <>
      <Section title="EAN / OEM Control Setup" tag="Stock status & transaction windows" color="#C0392B" colorLight="#FBEAE8" icon={iconControl}>
        <div className="im-grid2">
          <Field label="Stock type">
            <div className="im-segmented">
              {['inventory', 'non-inventory'].map(v => (
                <button key={v} type="button" className={form.invType === v ? 'active' : ''}
                  onClick={() => set('invType', v)}>{v === 'inventory' ? 'Inventory' : 'Non Inventory'}</button>))}
            </div>
          </Field>
          <Field label="Status">
            <div className="im-segmented">
              {['active', 'inactive'].map(v => (
                <button key={v} type="button" className={form.itemStatus === v ? 'active' : ''}
                  onClick={() => set('itemStatus', v)}>{v === 'active' ? 'Active' : 'Inactive'}</button>))}
            </div>
          </Field>
        </div>
        <div className="im-grid2" style={{ marginTop: 12 }}>
          <Field label="Accept Purchase">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input className="im-input mono" type="date" value={form.accPurchFrom} onChange={e => set('accPurchFrom', e.target.value)} />
              <span style={{ fontSize: 10, color: 'var(--ink-faint)', fontWeight: 600 }}>to</span>
              <input className="im-input mono" type="date" value={form.accPurchTo} onChange={e => set('accPurchTo', e.target.value)} />
            </div>
          </Field>
          <Field label="Accept Sales">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input className="im-input mono" type="date" value={form.accSalesFrom} onChange={e => set('accSalesFrom', e.target.value)} />
              <span style={{ fontSize: 10, color: 'var(--ink-faint)', fontWeight: 600 }}>to</span>
              <input className="im-input mono" type="date" value={form.accSalesTo} onChange={e => set('accSalesTo', e.target.value)} />
            </div>
          </Field>
        </div>
      </Section>

      <div className="im-row-2col">
        <Section title="Stock & Pricing Behaviour" color="#4C4FCB" colorLight="#EBEBFB" icon={iconSettings}>
          <Toggle label="Sell as gift voucher" sub="Skips stock deduction at billing" checked={form.giftVoucher} onChange={v => set('giftVoucher', v)} />
          <Toggle label="Reverse MRP calculation" sub="Derive base rate from MRP instead of the other way round" checked={form.reverseMrp} onChange={v => set('reverseMrp', v)} />
          <Toggle label="Use direct special MRP" sub="Skip auto-computed special MRP and enter it manually" checked={form.directSplMrp} onChange={v => set('directSplMrp', v)} />
          <div className="im-toggle-row">
            <div className="im-toggle-copy"><b>Apply excise duty</b><span>Adds an extra duty % on top of GST</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input className="im-input mono" type="number" value={form.exciseDuty} onChange={e => set('exciseDuty', e.target.value)} style={{ width: 56 }} disabled={!form.giftVoucher} />
              <label className="im-switch"><input type="checkbox" checked={!!form.exciseDuty && form.exciseDuty !== '0'} onChange={e => { if (!e.target.checked) set('exciseDuty', '0'); }} /><span className="im-track" /></label>
            </div>
          </div>
        </Section>

        <Section title="Entry Rules" color="#2E6BE0" colorLight="#EAF1FE" icon={iconEntry}>
          <Field label="Tax display mode">
            <div className="im-segmented">
              {['exclusive', 'inclusive'].map(v => (
                <button key={v} type="button" className={`${form.taxDisplayMode === v ? 'active' : ''} ${form.taxDisplayMode === v ? 'seg-pink' : ''}`}
                  onClick={() => set('taxDisplayMode', v)}>{v === 'exclusive' ? 'Exclusive' : 'Inclusive'}</button>))}
            </div>
          </Field>
          <div style={{ marginTop: 12 }}><Inp label="Date format" value="DD-MM-YYYY" onChange={() => {}} mono disabled /></div>
          <div style={{ fontSize: 10, color: 'var(--ink-faint)', margin: '2px 0 10px' }}>All date fields on this item follow this format.</div>
          <label style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ink-muted)' }}>Keyboard shortcuts</label>
          <div className="im-shortcut-chips">
            <div className="im-shortcut-chip"><kbd>F1</kbd>Save details</div>
            <div className="im-shortcut-chip"><kbd>F6</kbd>Focus item properties</div>
          </div>
        </Section>
      </div>
    </>
  );

  /* ─── Branding Tab ───────────────────────────────────────────────────────── */
  const renderBrandingTab = () => (
    <Section title="Branding & Promoter Support" color="#4C4FCB" colorLight="#EBEBFB" icon={iconBranding}>
      <div className="im-grid2">
        <Num label="Display Item Revenue" value={form.displayItemRevenue} onChange={v => set('displayItemRevenue', v)} />
        <LkpField label="Branding Image" value={form.brandingImage || 'No image attached'} readonlyVal={form.brandingImage || 'No image attached'} onLookup={() => {}} />
      </div>
      <Toggle label="Promoter support" sub="A brand promoter is stationed at this item's shelf" checked={form.promoterSupport} onChange={v => set('promoterSupport', v)} />
      <div className="im-grid3" style={{ marginTop: 12 }}>
        <Num label="Revenue 1" value={form.revenue1} onChange={v => set('revenue1', v)} />
        <Num label="Revenue 2" value={form.revenue2} onChange={v => set('revenue2', v)} />
        <Num label="Revenue 3" value={form.revenue3} onChange={v => set('revenue3', v)} />
      </div>
    </Section>
  );

  /* ─── Import Tab ─────────────────────────────────────────────────────────── */
  const renderImportTab = () => (
    <Section title="Bulk Import" tag=".xlsx / .csv"
      icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>}
      color="#0E8C7F" colorLight="#E4F5F2">
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <input className="im-input" readOnly value={importFile || ''} placeholder="No file selected" style={{ flex: 1 }} />
        <button type="button" className="im-btn im-btn-ghost" style={{ fontSize: 11 }} onClick={() => document.getElementById('im-file-input')?.click()}>Browse</button>
        <input id="im-file-input" type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={e => setImportFile(e.target.files?.[0]?.name || '')} />
      </div>
      <div className="im-empty-drop">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
        <b>Choose a file to preview rows here</b>
        Supported columns: SKU, Item Name, HSN, MRP, Rate, Supplier
      </div>
    </Section>
  );

  /* ─── Live Preview (right rail) ──────────────────────────────────────────── */
  const renderPreview = () => {
    const hasSku = !!form.skuPlu;
    const hasName = !!form.itemName;
    const hasCategory = aeValues && Object.keys(aeValues).some(k => k.toLowerCase().includes('division') || k.toLowerCase().includes('category'));
    const hasMrp = !!(parseFloat(form.mrp));
    const hasCost = !!(parseFloat(form.rate));
    const hasSupplier = !!form.supplierName;
    return (
      <>
        <div className="im-preview-card">
          <div className="im-preview-hero">
            <div className="im-preview-eyebrow"><span className="im-live-dot" />Live preview</div>
            <div className="im-preview-name">{form.itemName || 'Item Name'}</div>
            <div className="im-preview-art">{form.articleName || form.manufacturerName || 'Article · Category'}</div>
            <div className="im-preview-sku-row">
              {hasSku && <div className="im-sku-chip">{form.skuPlu}</div>}
              {form.hsnCode && <div className="im-sku-chip">HSN {form.hsnCode}</div>}
            </div>
            <BarcodeVisual code={form.eanCode} />
          </div>

          <div className="im-preview-price-block">
            <div className="im-price-row">
              <span className="im-price-main">{form.mrp ? `₹${parseFloat(form.mrp).toFixed(2)}` : '₹ —'}</span>
              {hasMrp && hasCost && <span className="im-disc-pill">margin {margin}%</span>}
            </div>
            <div className="im-price-sub">
              {form.listMrp && `List MRP ₹${parseFloat(form.listMrp).toFixed(2)} · `}
              {form.rate && `Cost ₹${parseFloat(form.rate).toFixed(2)} · `}
              {form.wsp && `Sales Price ₹${parseFloat(form.wsp).toFixed(2)}`}
            </div>
          </div>

          <div className="im-preview-stock">
            <div className="im-stock-label">
              <span>Stock band</span>
              <span>{form.rol} / {form.maxQty}</span>
            </div>
            <div className="im-stock-track"><div className="im-stock-fill" style={{ width: `${stockFill}%` }} /></div>
            <div className="im-stock-markers">
              <span>Min {form.minQty}</span><span>ROL {form.rol}</span><span>Max {form.maxQty}</span>
            </div>
          </div>

          <div className="im-preview-meta">
            <div className="im-meta-row"><span>Supplier</span><span>{form.supplierName || '—'}</span></div>
            <div className="im-meta-row"><span>Lead time</span><span>{form.leadTime ? `${form.leadTime} days` : '—'}</span></div>
            <div className="im-meta-row"><span>Shelf life</span><span>{form.expPeriod ? `${form.expPeriod} days` : '—'}</span></div>
            <div className="im-meta-row">
              <span>Tax</span>
              <span>{form.inputTaxName ? <span className="im-tax-tag">{form.inputTaxName}</span> : '—'}</span>
            </div>
          </div>

          <div className="im-history-grid">
            {[['Last Receipt', '—'], ['Last Purchased', '—'], ['Last Sales', '—'], ['Last Order', '—'], ['Last Price', form.rate ? `₹${form.rate}` : '—'], ['Old Mark Down', '0.00%'], ['Old Markup', '0.00%']].map(([lbl, val]) => (
              <div key={lbl} className="im-history-cell">
                <span className="hc-label">{lbl}</span>
                <span className="hc-val">{val}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="im-checklist">
          <h4>Before you save</h4>
          {[
            [hasSku && hasName, 'SKU & item name set'],
            [hasMrp && hasCost, 'MRP & cost rate entered'],
            [hasCategory, 'Category assigned'],
            [supplierRows.length > 0, 'Add at least one alt. supplier'],
            [false, 'Attach a product image'],
          ].map(([done, label]) => (
            <div key={label} className={`im-cl-item${done ? ' im-cl-done' : ' im-cl-todo'}`}>
              {done
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}
              {label}
            </div>
          ))}
        </div>
      </>
    );
  };

  /* ─── Lookup modal ───────────────────────────────────────────────────────── */
  /* ─── GetInfo popup renderer ─────────────────────────────────────────────── */
  const renderGetInfoPopup = () => {
    if (!getInfoState) return null;
    return (
      <GetInfoPopup
        title={getInfoState.title}
        tableName={getInfoState.tableName}
        screenId={getInfoState.screenId}
        controlId={getInfoState.controlId}
        compId={form.compId || 'C001'}
        onSelect={(row, cols, metaInfo) => {
          if (getInfoState.onSelect) getInfoState.onSelect(row, cols, metaInfo);
        }}
        onClose={() => setGetInfoState(null)}
      />
    );
  };

  /* ─── RENDER ─────────────────────────────────────────────────────────────── */
  const circumference = 2 * Math.PI * 16;
  const dashOffset = circumference - (profileStrength / 100) * circumference;

  return (
    <div className="im-root" data-testid="item-master-dynamic">
      {/* ── TOPBAR ── */}
      <div className="im-topbar">
        <div className="im-topbar-left">
          {onClose && (
            <button className="im-back-btn" onClick={onClose} data-testid="screen-back-btn">
              <ChevronLeft size={12} /> Back
            </button>
          )}
          <div>
            <div className="im-breadcrumb">Inventory <span>›</span> Item Master <span>›</span> <b>{editingItemId ? 'Edit Product' : 'New Product'}</b></div>
            <div className="im-title-row">
              <h1>Product Master</h1>
              <span className={`im-badge${editingItemId ? ' editing' : ''}`}>
                {editingItemId ? `editing · ${editingItemId}` : '1 item · draft'}
              </span>
            </div>
          </div>
          {/* Industry + Country context — moved from Classification section */}
          <div className="im-hdr-ctx">
            <div className="im-hdr-ctx-group">
              <span className="im-hdr-ctx-lbl">Industry</span>
              <select className="im-hdr-ctx-sel" value={selectedIndustry}
                onChange={e => { setSelectedIndustry(e.target.value); localStorage.setItem('ae_selected_industry', e.target.value); }}
                data-testid="industry-selector">
                {aeIndustries.map(i => <option key={i.IndustryId} value={i.IndustryId}>{i.IndustryName}</option>)}
                {!aeIndustries.length && <option value={selectedIndustry}>{selectedIndustry}</option>}
              </select>
            </div>
            {companyCountry && (
              <div className="im-hdr-ctx-country" title="Registered country" data-testid="company-country">
                {companyCountry}
              </div>
            )}
          </div>
        </div>
        <div className="im-topbar-right">
          {/* SKU search */}
          <div className="im-sku-search">
            <Search size={11} color="rgba(255,255,255,.5)" />
            <input value={searchSku} onChange={e => setSearchSku(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLoadItem()}
              placeholder="Search SKU / PLU to edit..." data-testid="sku-search-input" />
            <button className="im-btn im-btn-ghost" style={{ fontSize: 10, padding: '4px 9px' }} onClick={handleLoadItem} disabled={searchLoading} data-testid="sku-search-btn">
              {searchLoading ? '...' : 'Load'}
            </button>
            {editingItemId && <button className="im-btn im-btn-ghost" style={{ fontSize: 9, padding: '4px 7px' }} onClick={handleClear} title="New item">+ New</button>}
          </div>
          {/* Profile ring */}
          <div className="im-completeness">
            <div className="im-ring">
              <svg width="36" height="36" viewBox="0 0 38 38">
                <circle className="im-ring-bg" cx="19" cy="19" r="16" fill="none" strokeWidth="4" />
                <circle className="im-ring-fg" cx="19" cy="19" r="16" fill="none" strokeWidth="4"
                  strokeDasharray={circumference} strokeDashoffset={dashOffset} />
              </svg>
              <div className="im-ring-label">{profileStrength}%</div>
            </div>
            <div className="im-completeness-text">Profile<b>strength</b></div>
          </div>
          {/* Action buttons */}
          <button className="im-btn im-btn-ghost" onClick={handleClear}>
            <RotateCcw size={11} /> Clear
          </button>
          <button className="im-btn im-btn-primary" onClick={handleSave} disabled={saving} data-testid="item-save-btn">
            <Save size={11} /> {saving ? 'Saving...' : 'Save item'}
          </button>
        </div>
      </div>

      {/* ── ERROR BAR ── */}
      {error && <div className="im-error-bar" data-testid="item-error-bar">{error}</div>}

      {/* ── TABBAR ── */}
      <div className="im-tabbar">
        {visibleTabs.map(t => (
          <button key={t.id} className={`im-tab${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
            {TAB_ICONS[t.id]}{t.label}
          </button>
        ))}
      </div>

      {/* ── LAYOUT ── */}
      <div className="im-layout">
        {/* Main column */}
        <div className="im-col-main">
          <div className={`im-tab-panel${activeTab === 'sku' ? ' active' : ''}`}>{renderSKUTab()}</div>
          <div className={`im-tab-panel${activeTab === 'units' ? ' active' : ''}`}>{renderUnitsTab()}</div>
          <div className={`im-tab-panel${activeTab === 'rates' ? ' active' : ''}`}>{renderRatesTab()}</div>
          <div className={`im-tab-panel${activeTab === 'suppliers' ? ' active' : ''}`}>{renderSuppliersTab()}</div>
          <div className={`im-tab-panel${activeTab === 'control' ? ' active' : ''}`}>{renderControlTab()}</div>
          <div className={`im-tab-panel${activeTab === 'branding' ? ' active' : ''}`}>{renderBrandingTab()}</div>
          <div className={`im-tab-panel${activeTab === 'import' ? ' active' : ''}`}>{renderImportTab()}</div>
        </div>
        {/* Right rail: always visible */}
        <div className="im-col-preview">{renderPreview()}</div>
      </div>

      {renderGetInfoPopup()}
    </div>
  );
}
